import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { GetHelpApiService } from './get-help-api.service';
import { GetHelpDraft, GetHelpFlight, GetHelpIntroDto, GetHelpPhase } from './get-help.models';

/** Per-tab id for "this session": survives refreshes and token renewal, gone with the tab — the next login is a new id. */
const SESSION_ID_KEY = 'vfGetHelpSessionId';
/** Per-user, per-session progress: the existing-user timer's remaining time, whether it was cancelled, the draft. */
const SESSION_STATE_KEY_PREFIX = 'vfGetHelpIntro_';
/** Per-user, per-device cache of "done", so later page loads skip the BeginIntro round trip. The server stays the truth. */
const DONE_KEY_PREFIX = 'vfGetHelpIntroDone_';

/** Existing users: help moves into the menu after 5 s of My Day being on screen in an active tab. */
const AUTO_TRANSITION_MS = 5000;
/** The "Email draft opened" confirmation stays up this long before the banner moves into Get help. */
const SENT_HOLD_MS = 2000;
/** Design timing (Trovve.dc.html `hlpDismiss`): collapse + fly lands at 780 ms (inside the agreed 600–900 ms). */
const LAND_AFTER_MS = 780;
/** After arrival Get help stays highlighted for 2 s. */
const HIGHLIGHT_MS = 2000;

interface IntroSessionState {
    remainingMs: number;
    timerCancelled: boolean;
    draft: GetHelpDraft | null;
}

/**
 * The "Questions about Trovve?" introduction, shared between My Day's banner (MineComponent) and the always-mounted
 * shell (AppComponent: main-menu Get help, menu navigation, and the ? icon's flight).
 *
 * Who sees it is decided server-side (GetHelpAppService.BeginIntro) on the first My Day load: once per user, in one
 * browser session; a later session gets Get help straight in the menu. Within that session:
 *  - New users keep the banner until they close it or a question is sent.
 *  - Existing users also lose it after 5 s (counted only while My Day is on screen and the tab is visible) or on their
 *    first main-menu navigation — unless they hover, focus or use the banner / callout, which cancels that for the
 *    rest of the session.
 * Refreshing or coming back to My Day in the same session neither restarts the introduction nor resets the timer, and
 * a callout the user was working in comes back with their question.
 */
@Injectable({ providedIn: 'root' })
export class GetHelpStateService implements OnDestroy {
    private readonly phaseSubject = new BehaviorSubject<GetHelpPhase>('done');
    private readonly bannerMountedSubject = new BehaviorSubject<boolean>(false);
    private readonly flightSubject = new BehaviorSubject<GetHelpFlight | null>(null);
    private readonly destroy$ = new Subject<void>();

    private userId = 0;
    private isExistingUser = false;
    private introRequested = false;
    private bannerMounted = false;
    private bannerOnScreen = false;
    private bannerIcon: HTMLElement | null = null;
    private menuTarget: HTMLElement | null = null;
    private session: IntroSessionState = { remainingMs: AUTO_TRANSITION_MS, timerCancelled: false, draft: null };
    private autoTimer: ReturnType<typeof setTimeout> | null = null;
    private autoTimerStartedAt = 0;
    private sentTimer: ReturnType<typeof setTimeout> | null = null;
    private landTimer: ReturnType<typeof setTimeout> | null = null;
    private settleTimer: ReturnType<typeof setTimeout> | null = null;
    private readonly onVisibilityChange = (): void => this.syncAutoTimer();
    private readonly onPageHide = (): void => this.pauseAutoTimer();

    readonly phase$: Observable<GetHelpPhase> = this.phaseSubject.asObservable();
    readonly flight$: Observable<GetHelpFlight | null> = this.flightSubject.asObservable();

    /** Get help is in the main menu unless the banner is (or may be about to be) on screen. */
    readonly menuVisible$: Observable<boolean> = combineLatest([this.phaseSubject, this.bannerMountedSubject]).pipe(
        map(
            ([phase, mounted]) =>
                phase === 'landed' || phase === 'done' || ((phase === 'unknown' || phase === 'eligible') && !mounted)
        ),
        distinctUntilChanged()
    );

    /** The 2 s after the flight lands, when Get help is highlighted. */
    readonly menuLanding$: Observable<boolean> = this.phaseSubject.pipe(
        map((phase) => phase === 'landed'),
        distinctUntilChanged()
    );

    constructor(private api: GetHelpApiService) {}

    /** Called once by the app shell after login. */
    init(userId: number): void {
        this.userId = userId;
        this.session = this.readSessionState();
        this.phaseSubject.next(this.readLocal(DONE_KEY_PREFIX + userId) === '1' ? 'done' : 'unknown');
        document.addEventListener('visibilitychange', this.onVisibilityChange);
        window.addEventListener('pagehide', this.onPageHide);
    }

    /** My Day's banner asks, once per page load, whether to show the introduction. */
    ensureIntro(): void {
        if (this.phaseSubject.value !== 'unknown' || this.introRequested) {
            return;
        }
        this.introRequested = true;
        this.api
            .beginIntro({ sessionId: this.sessionId() })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (intro: GetHelpIntroDto) => {
                    if (!intro.showBanner) {
                        this.writeLocal(DONE_KEY_PREFIX + this.userId, '1');
                        this.phaseSubject.next('done');
                        return;
                    }
                    this.isExistingUser = intro.isExistingUser;
                    this.phaseSubject.next('eligible');
                },
                // Fail safe: no banner this page load, Get help in the menu; the next load asks again.
                error: (err: unknown) => {
                    abp.log.warn(err);
                    this.phaseSubject.next('done');
                },
            });
    }

    /**
     * My Day's banner reports whether it is mounted. The subject is deferred a microtask: this is called from the
     * banner's ngOnInit, inside the shell's change-detection pass, and menuVisible$ feeds the shell's template.
     */
    setBannerMounted(mounted: boolean): void {
        this.bannerMounted = mounted;
        if (!mounted) {
            this.bannerOnScreen = false;
            this.bannerIcon = null;
            this.pauseAutoTimer();
        }
        Promise.resolve().then(() => this.bannerMountedSubject.next(mounted));
    }

    /** The banner is rendered (My Day and the banner have loaded): the existing-user timer may start counting. */
    bannerShown(icon: HTMLElement | null): void {
        this.bannerIcon = icon;
        this.bannerOnScreen = true;
        this.syncAutoTimer();
    }

    /** The shell registers the element the ? icon flies towards (the profile block; Get help sits just above it). */
    registerMenuTarget(el: HTMLElement | null): void {
        this.menuTarget = el;
    }

    /** Hover, focus or any use of the banner or its callout: no automatic transition for the rest of this session. */
    cancelAutoTransition(): void {
        if (this.session.timerCancelled || this.phaseSubject.value !== 'eligible') {
            return;
        }
        this.pauseAutoTimer();
        this.session.timerCancelled = true;
        this.writeSessionState();
    }

    /** A main-menu navigation click. Existing users who have not engaged with help: help moves into the menu now. */
    onMainMenuNavigate(): void {
        if (this.phaseSubject.value === 'eligible' && this.isExistingUser && !this.session.timerCancelled) {
            this.transition();
        }
    }

    /** The banner's main ✕. */
    dismiss(): void {
        this.transition();
    }

    /** The question draft was opened in the user's mail app: hold the confirmation for 2 s, then move into Get help. */
    questionSent(): void {
        this.clearSentTimer();
        this.sentTimer = setTimeout(() => this.transition(), SENT_HOLD_MS);
    }

    getDraft(): GetHelpDraft | null {
        return this.session.draft;
    }

    saveDraft(draft: GetHelpDraft | null): void {
        this.session.draft = draft;
        this.writeSessionState();
    }

    ngOnDestroy(): void {
        this.clearTimers();
        this.pauseAutoTimer();
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        window.removeEventListener('pagehide', this.onPageHide);
        this.destroy$.next();
        this.destroy$.complete();
    }

    /** Ends the introduction for good and plays collapse + fly into Get help (or just the highlight when off My Day). */
    private transition(): void {
        let flight: GetHelpFlight | null;

        if (this.phaseSubject.value !== 'eligible') {
            return;
        }
        this.clearTimers();
        this.pauseAutoTimer();
        this.complete();
        if (!this.bannerOnScreen) {
            this.land();
            return;
        }
        flight = this.computeFlight();
        // No visible destination (no sidebar, e.g. the mobile layout): the banner's own collapse/fade is the transition.
        this.flightSubject.next(flight);
        this.phaseSubject.next('flying');
        this.landTimer = setTimeout(() => this.land(), LAND_AFTER_MS);
    }

    private land(): void {
        this.flightSubject.next(null);
        this.phaseSubject.next('landed');
        this.settleTimer = setTimeout(() => this.phaseSubject.next('done'), HIGHLIGHT_MS);
    }

    private complete(): void {
        this.writeLocal(DONE_KEY_PREFIX + this.userId, '1');
        this.session.draft = null;
        this.writeSessionState();
        this.api
            .completeIntro()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                // Best effort: if it fails, the next login ends the introduction anyway (BeginIntro from a new session).
                error: (err: unknown) => abp.log.warn(err),
            });
    }

    private computeFlight(): GetHelpFlight | null {
        let source: DOMRect;
        let target: DOMRect;
        let sx: number;
        let sy: number;

        if (!this.bannerIcon || !this.menuTarget) {
            return null;
        }
        source = this.bannerIcon.getBoundingClientRect();
        target = this.menuTarget.getBoundingClientRect();
        if (target.width === 0 || target.height === 0) {
            return null;
        }
        sx = source.left + source.width / 2;
        sy = source.top + source.height / 2;
        // Lands on the Get help icon: 27px in from the menu edge, 26px above the profile block (design `hlpDismiss`).
        return { x: sx - 16, y: sy - 16, dx: target.left + 27 - sx, dy: target.top - 26 - sy };
    }

    /** Runs the existing-user timer only while it should count: banner on screen, tab visible, not cancelled. */
    private syncAutoTimer(): void {
        const shouldRun =
            this.isExistingUser &&
            this.phaseSubject.value === 'eligible' &&
            !this.session.timerCancelled &&
            this.bannerMounted &&
            this.bannerOnScreen &&
            document.visibilityState === 'visible';
        if (!shouldRun) {
            this.pauseAutoTimer();
            return;
        }
        if (this.autoTimer) {
            return;
        }
        this.autoTimerStartedAt = Date.now();
        this.autoTimer = setTimeout(
            () => {
                this.autoTimer = null;
                this.session.remainingMs = 0;
                this.writeSessionState();
                this.transition();
            },
            Math.max(0, this.session.remainingMs)
        );
    }

    private pauseAutoTimer(): void {
        if (!this.autoTimer) {
            return;
        }
        clearTimeout(this.autoTimer);
        this.autoTimer = null;
        this.session.remainingMs = Math.max(0, this.session.remainingMs - (Date.now() - this.autoTimerStartedAt));
        this.writeSessionState();
    }

    private clearSentTimer(): void {
        if (this.sentTimer) {
            clearTimeout(this.sentTimer);
            this.sentTimer = null;
        }
    }

    private clearTimers(): void {
        this.clearSentTimer();
        if (this.landTimer) {
            clearTimeout(this.landTimer);
            this.landTimer = null;
        }
        if (this.settleTimer) {
            clearTimeout(this.settleTimer);
            this.settleTimer = null;
        }
    }

    private sessionId(): string {
        let id = this.readSession(SESSION_ID_KEY);
        if (!id) {
            id = Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 12);
            this.writeSession(SESSION_ID_KEY, id);
        }
        return id;
    }

    private readSessionState(): IntroSessionState {
        const fallback: IntroSessionState = { remainingMs: AUTO_TRANSITION_MS, timerCancelled: false, draft: null };
        const raw = this.readSession(SESSION_STATE_KEY_PREFIX + this.userId);
        if (!raw) {
            return fallback;
        }
        try {
            return { ...fallback, ...(JSON.parse(raw) as IntroSessionState) };
        } catch {
            return fallback;
        }
    }

    private writeSessionState(): void {
        this.writeSession(SESSION_STATE_KEY_PREFIX + this.userId, JSON.stringify(this.session));
    }

    // Storage can throw (blocked storage, private windows, some Teams webviews); the introduction must still work.
    private readSession(key: string): string | null {
        try {
            return window.sessionStorage.getItem(key);
        } catch {
            return null;
        }
    }

    private writeSession(key: string, value: string): void {
        try {
            window.sessionStorage.setItem(key, value);
        } catch {
            // ignore — see readSession
        }
    }

    private readLocal(key: string): string | null {
        try {
            return window.localStorage.getItem(key);
        } catch {
            return null;
        }
    }

    private writeLocal(key: string, value: string): void {
        try {
            window.localStorage.setItem(key, value);
        } catch {
            // ignore — see readSession
        }
    }
}
